/**
 * `/dosing-schedule` — custom dosing schedule (F2).
 *
 * CRUD over `DosingScheduleEntry` rows (product / amount in ml /
 * recurrence / optional time). Stored server-first via the
 * `dosing-schedule` UserDataStore namespace so it survives a browser
 * clear, device switch, and `pi-deploy`, and is captured in the F5
 * backup. Today's due doses surface on the dashboard agenda tile.
 */
import { useState } from 'react';

import {
  EMPTY_DOSING_SCHEDULE_DATA,
  describeRecurrence,
  type DosingScheduleData,
  type DosingScheduleEntry,
  type Recurrence,
} from '@reefapp/shared';

import { Card, CardBody, CardHeader, CardTitle } from '../components/Card';
import { RecurrenceEditor } from '../calendar/RecurrenceEditor';
import { useServerBackedState } from '../hooks/useServerBackedState';

interface EditorState {
  readonly mode: 'add' | 'edit';
  readonly entry: DosingScheduleEntry;
}

function blankEntry(): DosingScheduleEntry {
  return {
    id: crypto.randomUUID(),
    productName: '',
    amountMl: 1,
    recurrence: { kind: 'daily' },
  };
}

export function DosingSchedulePage() {
  const { value: data, setValue, loading } =
    useServerBackedState<DosingScheduleData>(
      'dosing-schedule',
      EMPTY_DOSING_SCHEDULE_DATA,
    );
  const [editor, setEditor] = useState<EditorState | null>(null);

  const entries = data.entries;

  const persist = (next: readonly DosingScheduleEntry[]): void => {
    setValue({ entries: next });
  };

  const saveEditor = (): void => {
    if (!editor) return;
    const productName = editor.entry.productName.trim();
    if (productName.length === 0) return;
    const entry: DosingScheduleEntry = { ...editor.entry, productName };
    if (editor.mode === 'add') {
      persist([...entries, entry]);
    } else {
      persist(entries.map((x) => (x.id === entry.id ? entry : x)));
    }
    setEditor(null);
  };

  const deleteEntry = (id: string): void => {
    persist(entries.filter((x) => x.id !== id));
  };

  return (
    <div className="min-h-screen px-6 py-8" data-testid="dosing-schedule-page">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Dosing Schedule</h1>
        <button
          type="button"
          onClick={() => setEditor({ mode: 'add', entry: blankEntry() })}
          className="reefapp-btn-secondary text-sm"
          data-testid="dosing-add-entry"
        >
          + Add dose
        </button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Scheduled doses</CardTitle>
        </CardHeader>
        <CardBody>
          {loading ? (
            <p className="text-sm text-reef-400">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-reef-400" data-testid="dosing-empty">
              No doses scheduled yet. Add a product, amount, and how often to
              dose it.
            </p>
          ) : (
            <ul className="flex flex-col gap-2" data-testid="dosing-entry-list">
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-reef-700 bg-reef-950/50 px-3 py-2"
                  data-testid={`dosing-entry-${entry.id}`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm text-reef-100">
                      {entry.productName} —{' '}
                      <span className="tabular-nums">{entry.amountMl} ml</span>
                    </div>
                    <div className="text-xs text-reef-400">
                      {describeRecurrence(entry.recurrence)}
                      {entry.time ? ` · ${entry.time}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setEditor({ mode: 'edit', entry })}
                      className="text-xs text-reef-300 hover:text-reef-100"
                      data-testid={`dosing-edit-${entry.id}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteEntry(entry.id)}
                      className="text-xs text-coral-400 hover:text-coral-500"
                      data-testid={`dosing-delete-${entry.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {editor ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          data-testid="dosing-editor-modal"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editor.mode === 'add' ? 'Add dose' : 'Edit dose'}
              </CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-xs text-reef-300">
                Product
                <input
                  type="text"
                  className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                  value={editor.entry.productName}
                  onChange={(ev) =>
                    setEditor({
                      ...editor,
                      entry: { ...editor.entry, productName: ev.target.value },
                    })
                  }
                  data-testid="dosing-editor-product"
                  placeholder="Alkalinity supplement"
                />
              </label>

              <label className="flex flex-col gap-1 text-xs text-reef-300">
                Amount (ml)
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  className="w-32 rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                  value={editor.entry.amountMl}
                  onChange={(ev) =>
                    setEditor({
                      ...editor,
                      entry: {
                        ...editor.entry,
                        amountMl: Math.max(0, Number(ev.target.value) || 0),
                      },
                    })
                  }
                  data-testid="dosing-editor-amount"
                />
              </label>

              <RecurrenceEditor
                value={editor.entry.recurrence}
                onChange={(recurrence: Recurrence) =>
                  setEditor({
                    ...editor,
                    entry: { ...editor.entry, recurrence },
                  })
                }
              />

              <label className="flex flex-col gap-1 text-xs text-reef-300">
                Time (optional)
                <input
                  type="time"
                  className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                  value={editor.entry.time ?? ''}
                  onChange={(ev) =>
                    setEditor({
                      ...editor,
                      entry: {
                        ...editor.entry,
                        time: ev.target.value || undefined,
                      },
                    })
                  }
                  data-testid="dosing-editor-time"
                />
              </label>

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="rounded-md border border-reef-700 bg-reef-900/60 px-3 py-1.5 text-sm text-reef-200 hover:bg-reef-800"
                  data-testid="dosing-editor-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEditor}
                  disabled={editor.entry.productName.trim().length === 0}
                  className="reefapp-btn-secondary text-sm disabled:opacity-50"
                  data-testid="dosing-editor-save"
                >
                  Save
                </button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
